import React, { useState, useEffect, useRef, ChangeEvent } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { Button } from '../button';
import { Modal } from '../modal';
import { URLS } from '../../../constants/urls';
import {
  GET_PRESIGNED_URL_FOR_BOOKING,
  UPLOAD_TRIP_PHOTO,
  DELETE_TRIP_PHOTO,
} from '../../services/bookingService/queries';

const Container = styled.div`
  ${tw`flex flex-col gap-4 w-full`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary`}
`;

const Section = styled.div`
  ${tw`flex flex-col gap-3`}
`;

const Label = styled.label`
  ${tw`text-sm font-medium text-text-secondary`}
`;

const SubText = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const PhotoCount = styled.div`
  ${tw`text-sm text-text-secondary font-medium mt-2`}
`;

const Actions = styled.div`
  ${tw`flex flex-col md:flex-row gap-3 mt-3`}
`;

const UploadLabel = styled.label<{ disabled?: boolean }>`
  ${tw`inline-flex items-center justify-center px-4 py-2 rounded-lg bg-black text-white cursor-pointer text-sm font-medium max-w-full min-w-0 box-border`}
  ${({ disabled }) => disabled && tw`opacity-60 cursor-not-allowed`}
  ${({ disabled }) => disabled && 'pointer-events: none;'}
`;

const UploadInput = styled.input`
  ${tw`hidden`}
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const UploaderGroup = styled.div`
  ${tw`mb-4`}
`;

const UploaderHeader = styled.div`
  ${tw`flex items-center gap-2 mb-2`}
`;

const UploaderAvatar = styled.div`
  ${tw`w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-sm font-bold`}
`;

const UploaderName = styled.span`
  ${tw`text-sm font-semibold text-text-primary`}
`;

const UploaderRole = styled.span`
  ${tw`text-xs text-text-secondary`}
`;

const PhotoTimestamp = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  text-align: center;
`;

const ScrollableSection = styled.div`
  max-height: 500px;
  overflow-y: auto;
  padding: 8px;
`;

const PhotoCard = styled.div`
  ${tw`relative rounded-lg overflow-hidden border border-border-default bg-white cursor-pointer`}
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* 1:1 aspect ratio */
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const PhotoImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveButton = styled.button`
  ${tw`absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold hover:bg-red-600`}
  border: none;
  z-index: 10;
`;

const DivideLine = styled.div`
  ${tw`w-full h-px bg-bg-secondary my-4`}
`;

const DeletedPhotosSection = styled.div`
  ${tw`mt-4`}
`;

const DeletedPhotosLabel = styled.div`
  ${tw`text-sm font-medium text-red-600 mb-2`}
`;

const DeletedPhotoItem = styled.div`
  ${tw`mb-3 pb-3 border-b border-border-default`}
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const DeletedPhotoLink = styled.a`
  ${tw`text-xs text-black hover:text-black-hover underline break-all block`}
  line-height: 1.6;
`;

const DeletedPhotoMeta = styled.div`
  ${tw`text-xs text-text-secondary mt-1`}
`;

// Full screen image viewer
const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const FullScreenImage = styled.img`
  max-width: 100vw;
  max-height: 100vh;
  object-fit: contain;
`;

const FullScreenCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

interface TripPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any;
  refetchBooking: () => void;
  isHost?: boolean;
}

export const TripPhotosModal: React.FC<TripPhotosModalProps> = ({
  isOpen,
  onClose,
  booking,
  refetchBooking,
  isHost = false,
}) => {
  const [tripStartFiles, setTripStartFiles] = useState<File[]>([]);
  const [tripStartPreviews, setTripStartPreviews] = useState<string[]>([]);
  const [tripEndFiles, setTripEndFiles] = useState<File[]>([]);
  const [tripEndPreviews, setTripEndPreviews] = useState<string[]>([]);
  const [isTripStartUploading, setIsTripStartUploading] = useState(false);
  const [isTripEndUploading, setIsTripEndUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const tripStartInputRef = useRef<HTMLInputElement>(null);
  const tripEndInputRef = useRef<HTMLInputElement>(null);

  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL_FOR_BOOKING);
  const [uploadTripPhoto] = useMutation(UPLOAD_TRIP_PHOTO);
  const [deleteTripPhoto] = useMutation(DELETE_TRIP_PHOTO, {
    refetchQueries: [],
  });

  const tripStartImages = booking?.tripStartImages || [];
  const tripEndImages = booking?.tripEndImages || [];
  const deletedTripStartImages = booking?.deletedTripStartImages || [];
  const deletedTripEndImages = booking?.deletedTripEndImages || [];

  useEffect(() => {
    if (!isOpen) {
      // Clean up previews when modal closes
      tripStartPreviews.forEach(url => URL.revokeObjectURL(url));
      tripEndPreviews.forEach(url => URL.revokeObjectURL(url));
      setTripStartFiles([]);
      setTripStartPreviews([]);
      setTripEndFiles([]);
      setTripEndPreviews([]);
      setSelectedImage(null);
    }

    return () => {
      tripStartPreviews.forEach(url => URL.revokeObjectURL(url));
      tripEndPreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [isOpen]);

  const totalTripStartCount = tripStartImages.length + tripStartFiles.length;
  const totalTripEndCount = tripEndImages.length + tripEndFiles.length;

  const hostId = booking?.hostId;
  const guestId = booking?.guestId;

  const groupByUploader = (images: any[]) => {
    const groups: Record<string, { name: string; role: string; images: any[] }> = {};
    images.forEach((image: any) => {
      const key = image.uploadedBy || 'unknown';
      if (!groups[key]) {
        const role = key === hostId ? 'Host' : key === guestId ? 'Guest' : '';
        groups[key] = {
          name: image.uploadedByName || 'Unknown',
          role,
          images: [],
        };
      }
      groups[key].images.push(image);
    });
    return Object.values(groups);
  };

  const formatTimestamp = (dateValue: any) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    return (
      date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
      ' at ' +
      date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    );
  };

  const canDeletePhoto = (image: any) => {
    if (isHost) return true;
    // Guest can only delete their own photos
    if (!image.uploadedBy) return true; // legacy photos without uploadedBy
    return image.uploadedBy === guestId;
  };

  const handleSelectPhotos = (
    event: ChangeEvent<HTMLInputElement>,
    imageType: 'tripStart' | 'tripEnd',
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const previews = files.map(file => URL.createObjectURL(file));

    if (imageType === 'tripStart') {
      setTripStartFiles(prev => [...prev, ...files]);
      setTripStartPreviews(prev => [...prev, ...previews]);
    } else {
      setTripEndFiles(prev => [...prev, ...files]);
      setTripEndPreviews(prev => [...prev, ...previews]);
    }

    event.target.value = '';
  };

  const uploadPhotos = async (imageType: 'tripStart' | 'tripEnd') => {
    const files = imageType === 'tripStart' ? tripStartFiles : tripEndFiles;
    if (!files.length) return;

    const setUploading =
      imageType === 'tripStart' ? setIsTripStartUploading : setIsTripEndUploading;
    setUploading(true);

    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const fileType = file.type || 'image/jpeg';
        const { data } = await getPresignedUrl({
          variables: {
            fileType,
            bookingId: booking.id,
            imageType,
          },
        });

        const { url, key } = data.getPresignedUrlForBooking;

        await fetch(url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': fileType,
          },
        });

        await uploadTripPhoto({
          variables: {
            input: {
              bookingId: booking.id,
              imageUrl: key,
              imageType,
              photoTimestamp: new Date(),
            },
          },
        });
      }

      if (imageType === 'tripStart') {
        tripStartPreviews.forEach(url => URL.revokeObjectURL(url));
        setTripStartFiles([]);
        setTripStartPreviews([]);
      } else {
        tripEndPreviews.forEach(url => URL.revokeObjectURL(url));
        setTripEndFiles([]);
        setTripEndPreviews([]);
      }

      await refetchBooking();
      toast.success('Trip photos uploaded');
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload trip photos');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (imageType: 'tripStart' | 'tripEnd', imageUrl: string) => {
    try {
      await deleteTripPhoto({
        variables: {
          input: {
            bookingId: booking.id,
            imageUrl,
            imageType,
          },
        },
      });
      await refetchBooking();
      toast.success('Photo deleted');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete photo');
    }
  };

  const handlePhotoClick = (imageUrl: string) => {
    const fullUrl = imageUrl.startsWith('http')
      ? imageUrl
      : `${URLS.S3_BUCKET.TRIP_PHOTOS}/${imageUrl}`;
    setSelectedImage(fullUrl);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} modalWidth="min(680px, calc(100vw - 20px))">
        <Container>
          <Title>Trip Photos</Title>

          {/* Trip Start Photos */}
          <Section>
            <Label>Trip Start Photos</Label>
            <SubText>Upload photos anytime. At least 6 photos are recommended.</SubText>

            {totalTripStartCount > 0 && (
              <PhotoCount>
                {totalTripStartCount} photo{totalTripStartCount !== 1 ? 's' : ''} selected
                {tripStartFiles.length > 0 && ` (${tripStartFiles.length} pending upload)`}
              </PhotoCount>
            )}

            <Actions>
              <UploadLabel htmlFor="trip-start-upload" disabled={isTripStartUploading}>
                Select Photos
              </UploadLabel>
              <UploadInput
                id="trip-start-upload"
                ref={tripStartInputRef}
                type="file"
                accept="image/*"
                multiple
                disabled={isTripStartUploading}
                onChange={event => handleSelectPhotos(event, 'tripStart')}
              />
              {tripStartFiles.length > 0 && (
                <Button
                  text={isTripStartUploading ? 'Uploading...' : 'Upload Now'}
                  onClick={() => uploadPhotos('tripStart')}
                  isDisabled={isTripStartUploading}
                  isLoading={isTripStartUploading}
                  buttonWidth="160px"
                />
              )}
            </Actions>

            {tripStartImages.length > 0 || tripStartPreviews.length > 0 ? (
              <ScrollableSection>
                {groupByUploader(tripStartImages).map((group, groupIndex) => (
                  <UploaderGroup key={`trip-start-group-${groupIndex}`}>
                    <UploaderHeader>
                      <UploaderAvatar>{group.name.charAt(0).toUpperCase()}</UploaderAvatar>
                      <UploaderName>{group.name}</UploaderName>
                      {group.role && <UploaderRole>({group.role})</UploaderRole>}
                    </UploaderHeader>
                    <PhotoGrid>
                      {group.images.map((image: any, index: number) => {
                        const imageUrl = image.url?.startsWith('http')
                          ? image.url
                          : `${URLS.S3_BUCKET.TRIP_PHOTOS}/${image.url}`;
                        return (
                          <PhotoCard key={`trip-start-${groupIndex}-${index}`}>
                            <PhotoImg
                              src={imageUrl}
                              alt={`Trip start ${index + 1}`}
                              onClick={() => handlePhotoClick(image.url)}
                            />
                            {canDeletePhoto(image) && (
                              <RemoveButton
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeletePhoto('tripStart', image.url);
                                }}
                                disabled={isTripStartUploading}
                              >
                                ×
                              </RemoveButton>
                            )}
                            <PhotoTimestamp>{formatTimestamp(image.createdAt)}</PhotoTimestamp>
                          </PhotoCard>
                        );
                      })}
                    </PhotoGrid>
                  </UploaderGroup>
                ))}
                {tripStartPreviews.length > 0 && (
                  <UploaderGroup>
                    <UploaderHeader>
                      <UploaderName>Pending Upload</UploaderName>
                    </UploaderHeader>
                    <PhotoGrid>
                      {tripStartPreviews.map((preview, index) => (
                        <PhotoCard key={`trip-start-preview-${index}`}>
                          <PhotoImg
                            src={preview}
                            alt={`Trip start preview ${index + 1}`}
                            onClick={() => handlePhotoClick(preview)}
                          />
                          <RemoveButton
                            onClick={e => {
                              e.stopPropagation();
                              URL.revokeObjectURL(preview);
                              setTripStartPreviews(prev => prev.filter((_, i) => i !== index));
                              setTripStartFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                            disabled={isTripStartUploading}
                          >
                            ×
                          </RemoveButton>
                        </PhotoCard>
                      ))}
                    </PhotoGrid>
                  </UploaderGroup>
                )}
              </ScrollableSection>
            ) : (
              <SubText>No trip start photos yet.</SubText>
            )}

            {isHost && deletedTripStartImages.length > 0 && (
              <DeletedPhotosSection>
                <DeletedPhotosLabel>Deleted Photos:</DeletedPhotosLabel>
                {deletedTripStartImages.map((image: any, index: number) => {
                  const imageUrl = image.url?.startsWith('http')
                    ? image.url
                    : `${URLS.S3_BUCKET.TRIP_PHOTOS}/${image.url}`;
                  return (
                    <DeletedPhotoItem key={`deleted-trip-start-${index}`}>
                      <DeletedPhotoLink href={imageUrl} target="_blank" rel="noopener noreferrer">
                        {imageUrl}
                      </DeletedPhotoLink>
                      <DeletedPhotoMeta>
                        {(image.uploadedByName || image.uploadedBy) && (
                          <>Uploaded by: {image.uploadedByName || image.uploadedBy}</>
                        )}
                        {image.createdAt && <> at {formatTimestamp(image.createdAt)}</>}
                      </DeletedPhotoMeta>
                      <DeletedPhotoMeta>
                        {(image.deletedByName || image.deletedBy) && (
                          <>Deleted by: {image.deletedByName || image.deletedBy}</>
                        )}
                        {image.deletedAt && <> at {formatTimestamp(image.deletedAt)}</>}
                      </DeletedPhotoMeta>
                    </DeletedPhotoItem>
                  );
                })}
              </DeletedPhotosSection>
            )}
          </Section>

          <DivideLine />

          {/* Trip End Photos */}
          <Section>
            <Label>Trip End Photos</Label>
            <SubText>Upload photos anytime after the trip ends.</SubText>

            {totalTripEndCount > 0 && (
              <PhotoCount>
                {totalTripEndCount} photo{totalTripEndCount !== 1 ? 's' : ''} selected
                {tripEndFiles.length > 0 && ` (${tripEndFiles.length} pending upload)`}
              </PhotoCount>
            )}

            <Actions>
              <UploadLabel htmlFor="trip-end-upload" disabled={isTripEndUploading}>
                Select Photos
              </UploadLabel>
              <UploadInput
                id="trip-end-upload"
                ref={tripEndInputRef}
                type="file"
                accept="image/*"
                multiple
                disabled={isTripEndUploading}
                onChange={event => handleSelectPhotos(event, 'tripEnd')}
              />
              {tripEndFiles.length > 0 && (
                <Button
                  text={isTripEndUploading ? 'Uploading...' : 'Upload Now'}
                  onClick={() => uploadPhotos('tripEnd')}
                  isDisabled={isTripEndUploading}
                  isLoading={isTripEndUploading}
                  buttonWidth="160px"
                />
              )}
            </Actions>

            {tripEndImages.length > 0 || tripEndPreviews.length > 0 ? (
              <ScrollableSection>
                {groupByUploader(tripEndImages).map((group, groupIndex) => (
                  <UploaderGroup key={`trip-end-group-${groupIndex}`}>
                    <UploaderHeader>
                      <UploaderAvatar>{group.name.charAt(0).toUpperCase()}</UploaderAvatar>
                      <UploaderName>{group.name}</UploaderName>
                      {group.role && <UploaderRole>({group.role})</UploaderRole>}
                    </UploaderHeader>
                    <PhotoGrid>
                      {group.images.map((image: any, index: number) => {
                        const imageUrl = image.url?.startsWith('http')
                          ? image.url
                          : `${URLS.S3_BUCKET.TRIP_PHOTOS}/${image.url}`;
                        return (
                          <PhotoCard key={`trip-end-${groupIndex}-${index}`}>
                            <PhotoImg
                              src={imageUrl}
                              alt={`Trip end ${index + 1}`}
                              onClick={() => handlePhotoClick(image.url)}
                            />
                            {canDeletePhoto(image) && (
                              <RemoveButton
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeletePhoto('tripEnd', image.url);
                                }}
                                disabled={isTripEndUploading}
                              >
                                ×
                              </RemoveButton>
                            )}
                            <PhotoTimestamp>{formatTimestamp(image.createdAt)}</PhotoTimestamp>
                          </PhotoCard>
                        );
                      })}
                    </PhotoGrid>
                  </UploaderGroup>
                ))}
                {tripEndPreviews.length > 0 && (
                  <UploaderGroup>
                    <UploaderHeader>
                      <UploaderName>Pending Upload</UploaderName>
                    </UploaderHeader>
                    <PhotoGrid>
                      {tripEndPreviews.map((preview, index) => (
                        <PhotoCard key={`trip-end-preview-${index}`}>
                          <PhotoImg
                            src={preview}
                            alt={`Trip end preview ${index + 1}`}
                            onClick={() => handlePhotoClick(preview)}
                          />
                          <RemoveButton
                            onClick={e => {
                              e.stopPropagation();
                              URL.revokeObjectURL(preview);
                              setTripEndPreviews(prev => prev.filter((_, i) => i !== index));
                              setTripEndFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                            disabled={isTripEndUploading}
                          >
                            ×
                          </RemoveButton>
                        </PhotoCard>
                      ))}
                    </PhotoGrid>
                  </UploaderGroup>
                )}
              </ScrollableSection>
            ) : (
              <SubText>No trip end photos yet.</SubText>
            )}

            {isHost && deletedTripEndImages.length > 0 && (
              <DeletedPhotosSection>
                <DeletedPhotosLabel>Deleted Photos:</DeletedPhotosLabel>
                {deletedTripEndImages.map((image: any, index: number) => {
                  const imageUrl = image.url?.startsWith('http')
                    ? image.url
                    : `${URLS.S3_BUCKET.TRIP_PHOTOS}/${image.url}`;
                  return (
                    <DeletedPhotoItem key={`deleted-trip-end-${index}`}>
                      <DeletedPhotoLink href={imageUrl} target="_blank" rel="noopener noreferrer">
                        {imageUrl}
                      </DeletedPhotoLink>
                      <DeletedPhotoMeta>
                        {(image.uploadedByName || image.uploadedBy) && (
                          <>Uploaded by: {image.uploadedByName || image.uploadedBy}</>
                        )}
                        {image.createdAt && <> at {formatTimestamp(image.createdAt)}</>}
                      </DeletedPhotoMeta>
                      <DeletedPhotoMeta>
                        {(image.deletedByName || image.deletedBy) && (
                          <>Deleted by: {image.deletedByName || image.deletedBy}</>
                        )}
                        {image.deletedAt && <> at {formatTimestamp(image.deletedAt)}</>}
                      </DeletedPhotoMeta>
                    </DeletedPhotoItem>
                  );
                })}
              </DeletedPhotosSection>
            )}
          </Section>
        </Container>
      </Modal>

      {/* Full Screen Image Viewer */}
      {selectedImage && (
        <FullScreenOverlay onClick={() => setSelectedImage(null)}>
          <FullScreenCloseButton onClick={() => setSelectedImage(null)}>×</FullScreenCloseButton>
          <FullScreenImage
            src={selectedImage}
            alt="Full screen view"
            onClick={e => e.stopPropagation()}
          />
        </FullScreenOverlay>
      )}
    </>
  );
};
